import Loader from 'components/Loader';
import TableError from 'components/TableError';
import useRequest from 'hooks/useRequest';
import { useRouter } from 'hooks/useRouter';
import { isEmpty } from 'lodash';
import * as moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  Table,
  TabPane,
  UncontrolledDropdown,
} from 'reactstrap';
import confirm from 'reactstrap-confirm';
import { consultationStatusEnum } from 'utils/constants';
import { SMKLinks } from 'utils/links';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import Paginate from '../../components/Paginate';
import {
  buildURL,
  checkIfValueExist,
  formatInputDate,
  printBirthDifference,
  printDateTimeTwoLines,
  printSerialNumbers,
  titleCaseIfExists,
} from '../../utils/helper';
import UpdateStatusModal from './components/updateStatusModal';

const ConsultationsList = () => {
  const history = useHistory();
  const router = useRouter();
  toast.configure();

  const [activeTab, setActiveTab] = useState(consultationStatusEnum.REQUESTED);
  const tabsList = [
    {
      name: 'Requested',
      filters: {
        status: consultationStatusEnum.REQUESTED,
        deleted: false,
      },
    },
    {
      name: 'Scheduled',
      filters: {
        status: consultationStatusEnum.SCHEDULED,
        deleted: false,
      },
    },
    {
      name: 'OnGoing',
      filters: {
        status: consultationStatusEnum.ONGOING,
        deleted: false,
      },
    },
    {
      name: 'Completed',
      filters: {
        status: consultationStatusEnum.COMPLETED,
        deleted: false,
      },
    },
    {
      name: 'Closed',
      filters: {
        status: consultationStatusEnum.CLOSED,
        deleted: false,
      },
    },
    {
      name: 'Cancelled',
      filters: {
        status: consultationStatusEnum.CANCELLED,
        deleted: false,
      },
    },
    {
      name: 'Deleted',
      filters: {
        deleted: true,
      },
    },
    {
      name: 'All',
      filters: {},
    },
  ];

  const [inquiryList, setInquiryList] = useState([]);
  const [inquiryPagination, setInquiryPagination] = useState({});

  const [deleteEnquiryRequest, { loading: deleteEnquiryLoading }] = useRequest({
    method: 'DELETE',
    onSuccess: () => {
      const currentTabFilter = tabsList?.find((tab) => tab.name?.toLowerCase() === activeTab?.toLowerCase());
      handleChangePage(1, currentTabFilter?.filters);
      toast.success('Enquiry has been deleted');
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });
  const [updateEnquiryRequest, { loading: updateEnquiryLoading }] = useRequest({
    method: 'PUT',
    onSuccess: () => {
      const currentTabFilter = tabsList?.find((tab) => tab.name?.toLowerCase() === activeTab?.toLowerCase());
      handleChangePage(1, currentTabFilter?.filters);
      toast.success('Enquiry has been updated.');
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const [getInquiryListRequest, { loading: getInquiryListLoading }] = useRequest({
    method: 'POST',
    url: '/consultation/admin/search',
    onSuccess: (data) => {
      setInquiryList(data?.results);
      setInquiryPagination(data?.pagination);
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const defaultFilters = {
    deleted: { value: null, label: 'All' },
    startDate: formatInputDate(moment().subtract(1, 'year')),
    endDate: formatInputDate(moment()),
  };

  const [filters, setFilters] = useState(defaultFilters);

  const setPageInUrl = (page) => {
    history.push({
      pathname: router.pathname,
      search: `?${new URLSearchParams({ page }).toString()}`,
    });
  };

  const handleChangePage = (page, tabFilters) => {
    setPageInUrl(page);
    getInquiryListRequest({
      body: {
        page,
        size: 50,
        ...filters,
        sortBy: 'updatedDate',
        sortOrder: -1,
        deleted: checkIfValueExist(filters.deleted?.value),
        ...tabFilters,
      },
    });
  };

  const handleMarkClosed = async (data) => {
    const result = await confirm({
      title: 'Mark Completed',
      message: 'Are you sure, you want to mark completed taken on this Inquiry?',
      confirmText: 'Confirm',
      confirmColor: 'primary',
      cancelColor: 'link text-danger',
    });

    if (result) {
      updateEnquiryRequest({
        url: `/consultation/admin/${data?.id}/mark-completed`,
      });
    }
  };

  const handleDeleteInquiry = async (data) => {
    const result = await confirm({
      title: 'Delete Inquiry',
      message: 'Are you sure, you want to delete this Inquiry?',
      confirmText: 'Confirm',
      confirmColor: 'primary',
      cancelColor: 'link text-danger',
    });

    if (result) {
      deleteEnquiryRequest({
        url: `/consultation/admin/${data?.id}`,
      });
    }
  };

  const applyFilters = () => {
    const currentTabFilter = tabsList?.find((tab) => tab.name?.toLowerCase() === activeTab?.toLowerCase());
    handleChangePage(parseInt(router?.query?.page, 10) || 1, currentTabFilter?.filters);
  };

  const clearFilters = () => {
    const currentTabFilter = tabsList?.find((tab) => tab.name?.toLowerCase() === activeTab?.toLowerCase());

    setFilters({
      ...defaultFilters,
    });
    getInquiryListRequest({
      body: {
        page: 1,
        size: 50,
        ...defaultFilters,
        sortBy: 'updatedDate',
        sortOrder: -1,
        deleted: checkIfValueExist(defaultFilters.deleted?.value),
        ...currentTabFilter?.filters,
      },
    });
  };

  const handleChangePagination = (page) => {
    const currentTabFilter = tabsList?.find((tab) => tab.name?.toLowerCase() === activeTab?.toLowerCase());
    handleChangePage(page, currentTabFilter?.filters);
  };

  const [openUpdateStatusModal, setOpenUpdateStatusModal] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);

  const toggleUpdateStatusModal = () => {
    setOpenUpdateStatusModal(!openUpdateStatusModal);
  };

  const handleUpdateStatus = (data) => {
    setSelectedConsultation(data);
    toggleUpdateStatusModal();
  };

  useEffect(() => {
    const currentTabFilter = tabsList?.find((tab) => tab.name?.toLowerCase() === activeTab?.toLowerCase());
    handleChangePage(parseInt(router?.query?.page, 10) || 1, currentTabFilter?.filters);
  }, []);

  return (
    <>
      <Loader isActive={getInquiryListLoading || deleteEnquiryLoading || updateEnquiryLoading} />
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Consultations" breadcrumbItem="Consultations Tickets List" />

          <div>
            <Nav tabs>
              {tabsList?.map((elem, index) => {
                return (
                  <NavItem key={index}>
                    <NavLink
                      className={activeTab?.toLowerCase() === elem?.name?.toLowerCase() ? 'active bg-white' : ''}
                      onClick={() => {
                        setActiveTab(elem?.name?.toLowerCase());
                        handleChangePage(1, elem?.filters);
                      }}>
                      {elem?.name}
                    </NavLink>
                  </NavItem>
                );
              })}
            </Nav>

            <TabContent activeTab={activeTab}>
              <TabPane tabId={activeTab}>
                <Card>
                  <CardBody>
                    <div className="mt-4">
                      <div className="row">
                        <div className="col-lg-3 mb-4">
                          <label className="form-label">Created Date - From</label>
                          <Input
                            type="date"
                            name="startDate"
                            placeholder="From Date"
                            value={filters.startDate}
                            max={moment().format('YYYY-MM-DD')}
                            onChange={(e) => setFilters({ ...filters, startDate: formatInputDate(e.target.value) })}
                          />
                        </div>
                        <div className="col-lg-3 mb-4">
                          <label className="form-label">Created Date - To</label>
                          <Input
                            type="date"
                            name="endDate"
                            placeholder="To Date"
                            value={filters.endDate}
                            max={moment().format('YYYY-MM-DD')}
                            onChange={(e) => setFilters({ ...filters, endDate: formatInputDate(e.target.value) })}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <a className="btn btn-primary me-4" onClick={applyFilters}>
                        Apply Filters
                      </a>
                      <a className="" onClick={clearFilters}>
                        Clear
                      </a>
                    </div>
                  </CardBody>
                </Card>
              </TabPane>
            </TabContent>
          </div>

          {inquiryPagination && (
            <Row className="mb-2">
              <Col sm="4">
                <div className="search-box me-2 mb-2 d-inline-block">
                  <div className="position-relative font-bold">{inquiryPagination?.totalCount} Tickets Found</div>
                </div>
              </Col>
            </Row>
          )}

          <Row>
            <Col xs="12">
              <Col lg="12">
                <div className="">
                  <div className="table-responsive" style={{ minHeight: 300 }}>
                    <Table className="project-list-table table-centered table-borderless">
                      <thead>
                        <tr className="table-warning">
                          {' '}
                          <th scope="col">S.No.</th>
                          {/* <th scope="col">Patient DOB </th> */}
                          <th scope="col">Patient Name</th>
                          <th scope="col">Question</th>
                          {/* <th scope="col">Doctor Name </th>
                          <th scope="col">Doctor Expiry Date </th> */}
                          <th scope="col">Speciality</th> <th scope="col">paymentStatus</th>
                          {/* <th scope="col">Symptoms</th> */}
                          {/* <th scope="col">Medical History</th> */}
                          <th scope="col">Request Date</th>
                          <th scope="col">Updated Date</th>
                          {/* <th scope="col">PrescriptionId</th> */}
                          {/* <th scope="col">isPregnancy</th> */}
                          {/* <th scope="col">Approved</th> */}
                          <th scope="col" />
                        </tr>
                      </thead>
                      <tbody>
                        {!isEmpty(inquiryList) ? (
                          inquiryList?.map((data, index) => (
                            <tr
                              className={
                                data?.status === consultationStatusEnum.COMPLETED
                                  ? 'bg-success'
                                  : data?.deleted || data?.status === consultationStatusEnum.CANCELLED
                                  ? 'bg-danger'
                                  : ''
                              }
                              key={index}>
                              <td>{printSerialNumbers(inquiryPagination)[index]}</td>
                              <td style={{ minWidth: 300 }}>
                                <Link
                                  to={buildURL(SMKLinks.CONSULTATION_DETAILS, {
                                    id: data?.id,
                                  })}>
                                  {data?.patient?.firstName} {data?.patient?.lastName}
                                </Link>
                                <br />
                                Age: {printBirthDifference(data?.patient?.dob)}
                                <br />
                                <Link
                                  to={buildURL(SMKLinks.USER_PROFILE, {
                                    id: data?.createdBy?.id,
                                  })}>
                                  {data?.createdBy?.displayName} - {data?.createdBy?.id}
                                </Link>
                              </td>
                              <td>
                                {!isEmpty(data?.description)
                                  ? `${
                                      data?.description.length >= 50
                                        ? data?.description?.slice(0, 50) + '...'
                                        : data?.description
                                    }`
                                  : ''}
                              </td>
                              {/* <td>
                                {data?.doctor?.firstName} {data?.doctor?.lastName}
                              </td>
                              <td className="text-nowrap">{printDateTimeTwoLines(data.expiryDate)}</td> */}
                              <td>
                                <Badge className={'bg-light'} size={'sm'}>
                                  {titleCaseIfExists(data?.speciality?.name)?.replaceAll('_', ' ')}
                                </Badge>
                              </td>{' '}
                              <td className="text-nowrap">
                                {data?.feeAmount} ({data?.paymentStatus})
                              </td>
                              {/* <td>
                                {data?.symptoms?.map((e, idx) => (
                                  <Badge className={'bg-light'} size={'sm'} key={idx}>
                                    {e.replaceAll('-', ' ')}
                                  </Badge>
                                ))}
                              </td>
                              <td>
                                {data?.medicalHistory
                                  ?.filter((e) => e !== 'none')
                                  ?.map((e, idx) => (
                                    <Badge className={'bg-light me-1'} size={'sm'} key={idx}>
                                      {e.replaceAll('_', ' ')}
                                    </Badge>
                                  ))}
                              </td> */}
                              <td className="text-nowrap">{printDateTimeTwoLines(data.createdDate)}</td>
                              <td className="text-nowrap">{printDateTimeTwoLines(data.updatedDate)}</td>
                              {/* <td>{data.prescriptionId}</td>
                              <td>
                                <Badge className={data.pregnancy ? 'bg-success' : 'bg-danger'}>
                                  {data.pregnancy ? 'Yes' : 'No'}
                                </Badge>
                              </td> */}
                              {/* <td>
                                <Badge className={data.approved ? 'bg-success' : 'bg-danger'}>
                                  {data.approved ? 'Yes' : 'No'}
                                </Badge>
                              </td> */}
                              <td>
                                {!data?.deleted && (
                                  <UncontrolledDropdown>
                                    <DropdownToggle href="#" className="card-drop" tag="i">
                                      <i role="button" className="mdi mdi-dots-vertical font-size-18" />
                                    </DropdownToggle>
                                    <DropdownMenu className="dropdown-menu-end">
                                      <DropdownItem onClick={() => handleUpdateStatus(data)}>
                                        <i className="fas fa-pencil-alt text-success me-2 fa-fw" />
                                        Update Status
                                      </DropdownItem>
                                      <Link
                                        to={buildURL(SMKLinks.CONSULTATION_DETAILS, {
                                          id: data?.id,
                                        })}>
                                        <DropdownItem>
                                          <i className="mdi mdi-eye text-primary me-2" />
                                          View Details
                                        </DropdownItem>
                                      </Link>

                                      {!data?.deleted && (
                                        <DropdownItem onClick={() => handleDeleteInquiry(data)}>
                                          <i className="fas fa-trash-alt text-danger me-2 fa-fw" />
                                          Delete Enquiry
                                        </DropdownItem>
                                      )}
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <TableError />
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </Col>
            </Col>
          </Row>

          <Paginate pageInfo={inquiryPagination} onChangePage={handleChangePagination} />

          {/* Update status modal  */}
          <UpdateStatusModal
            isOpen={openUpdateStatusModal}
            toggle={toggleUpdateStatusModal}
            selectedItem={selectedConsultation}
          />
        </Container>
      </div>
    </>
  );
};

export default ConsultationsList;
